import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarDays } from "lucide-react";
import { toast } from "react-toastify";
import PaginationComponent from "../components/PaginationComponent";

import AddBankingTransactionDialog from "../components/banking/AddBankingTransactionDialog";
import BankingTransactionsTable from "../components/banking/BankingTransactionsTable";
import { useGetAllBranches, useGetCounters } from "../service/branchService";
import {
  CreateTransactionPayload,
  Transaction,
  useGetAllBankingTransactions,
  usePostBankingTransaction,
} from "../service/bankingService";
import { useGetAllUsers } from "../service/userService";
import { Button } from "../utils/components/ui/button";
import { Input } from "../utils/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../utils/components/ui/select";

type TransactionType = "Cash In" | "Cash Out" | "Bank In" | "Bank Out";
type TransactionMode = "Cash" | "Bank";
type ApiTransactionType = CreateTransactionPayload["transactionType"];

type BankingFormState = {
  referenceId: string;
  transactionDate: string;
  transactionType: string;
  mode: string;
  category: string;
  amount: string;
  remarks: string;
};

const MODE_OPTIONS: TransactionMode[] = ["Cash", "Bank"];
const TRANSACTION_TYPES_BY_MODE: Record<TransactionMode, TransactionType[]> = {
  Cash: ["Cash In", "Cash Out"],
  Bank: ["Bank In", "Bank Out"],
};
const API_TRANSACTION_TYPE_BY_LABEL: Record<
  TransactionType,
  ApiTransactionType
> = {
  "Cash In": "CASH_IN",
  "Cash Out": "CASH_OUT",
  "Bank In": "BANK_IN",
  "Bank Out": "BANK_OUT",
};
const CATEGORY_OPTIONS = [
  { label: "Expense", value: "EXPENSE" },
  { label: "Transfer", value: "TRANSFER" },
] as const;

const createInitialFormState = (): BankingFormState => ({
  referenceId: "",
  transactionDate: new Date().toISOString().slice(0, 10),
  transactionType: "",
  mode: "",
  category: "",
  amount: "",
  remarks: "",
});

const formatDisplayDate = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const formatTableDate = (value: string) =>
  formatDisplayDate(value).replace(/\//g, "-");

const formatCurrency = (value: number) => {
  if (value === 0) {
    return "0";
  }

  return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(value);
};

function Banking() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedBranch, setSelectedBranch] = useState(
    localStorage.getItem("branchId") || "",
  );
  const [selectedCounter, setSelectedCounter] = useState(
    localStorage.getItem("counterId") || "",
  );
  const [selectedStaff, setSelectedStaff] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formState, setFormState] = useState<BankingFormState>(
    createInitialFormState,
  );

  const { data: branchData } = useGetAllBranches({
    params: {
      search: "",
      isActive: true,
      page: 0,
      size: 100,
      sortBy: "branchName",
      sortDir: "asc",
    },
  });

  const { data: counterData } = useGetCounters({
    params: {
      branchId: selectedBranch,
      search: "",
      isActive: true,
      page: 0,
      size: 100,
      sortBy: "counterName",
      sortDir: "asc",
    },
  });

  const { data: userData } = useGetAllUsers({
    userName: "",
    pageNo: 0,
    pageSize: 100,
  });

  const branches = branchData?.content ?? [];
  const counters = counterData?.content ?? [];
  const staffs = (userData?.userDetailsResponse ?? []) as Array<{
    id: number;
    username: string;
  }>;
  const handleResetFilters = () => {
    setSelectedBranch("");
    setSelectedCounter("");
    setSelectedStaff("");
    setSelectedDate("");
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedBranch, selectedCounter, selectedStaff, selectedDate, pageSize]);

  const bankingQueryParams = useMemo(
    () => ({
      branchId: Number(selectedBranch) || 0,
      counterId: Number(selectedCounter) || 0,
      staffId: Number(selectedStaff) || null,
      date: selectedDate,
      pageNo: currentPage,
      pageSize,
    }),
    [
      currentPage,
      pageSize,
      selectedBranch,
      selectedCounter,
      selectedDate,
      selectedStaff,
    ],
  );

  const {
    data: bankingTransactionsData,
    isLoading: isBankingTransactionsLoading,
  } = useGetAllBankingTransactions({
    params: bankingQueryParams,
  });
  const { mutate: postBankingTransaction, isPending: isPostingTransaction } =
    usePostBankingTransaction();

  const transactions = useMemo(
    () =>
      (bankingTransactionsData?.transactions?.content ?? []).map(
        (transaction: Transaction) => ({
          ...transaction,
          referenceId: transaction.referenceNumber,
        }),
      ),
    [bankingTransactionsData?.transactions?.content],
  );

  const totals = useMemo(
    () => ({
      cashIn: bankingTransactionsData?.pageCashIn ?? 0,
      cashOut: bankingTransactionsData?.pageCashOut ?? 0,
    }),
    [bankingTransactionsData?.pageCashIn, bankingTransactionsData?.pageCashOut],
  );

  const totalElements =
    bankingTransactionsData?.transactions?.totalElements ?? 0;
  const totalPages = bankingTransactionsData?.transactions?.totalPages ?? 0;



  const updateForm = (field: keyof BankingFormState, value: string) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
      ...(field === "mode" &&
      !TRANSACTION_TYPES_BY_MODE[value as TransactionMode]?.includes(
        current.transactionType as TransactionType,
      )
        ? { transactionType: "" }
        : {}),
    }));
  };

  const resetForm = () => {
    setFormState(createInitialFormState());
  };

  const handleCreateTransaction = () => {
    const amount = Number(formState.amount);

    if (
      !formState.referenceId ||
      !formState.transactionDate ||
      !formState.transactionType ||
      !formState.mode ||
      !formState.category ||
      !formState.amount
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (Number.isNaN(amount) || amount <= 0) {
      toast.error("Amount must be greater than zero.");
      return;
    }

    const payload: CreateTransactionPayload = {
      referenceNumber: formState.referenceId,
      transactionDate: formState.transactionDate,
      transactionType:
        API_TRANSACTION_TYPE_BY_LABEL[
          formState.transactionType as TransactionType
        ],
      mode: formState.mode.toUpperCase(),
      category: formState.category,
      referenceId: formState.referenceId,
      amount,
      remarks: formState.remarks,
    };

    postBankingTransaction(payload, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["useGetAllBankingTransactions"],
        });
        setIsDialogOpen(false);
        resetForm();
        toast.success("Transaction created successfully.");
      },
      onError: (error: Error) => {
        toast.error(`Failed to create transaction: ${error.message}`);
      },
    });
  };

  const handleExport = () => {
    const csvRows = [
      [
        "Transaction date",
        "Transaction type",
        "Mode",
        "Category",
        "Reference ID",
        "Cash In",
        "Cash Out",
        "Remarks",
        "Created By",
      ],
      ...transactions.map((transaction) => [
        formatTableDate(transaction.transactionDate),
        transaction.transactionType,
        transaction.mode,
        transaction.category,
        transaction.referenceId,
        transaction.cashIn || "--",
        transaction.cashOut || "--",
        transaction.remarks,
        transaction.createdBy,
      ]),
      ["Total", "", "", "", "", totals.cashIn, totals.cashOut, "", ""],
    ];

    const csvContent = csvRows
      .map((row) => row.map((value) => `"${String(value)}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "banking-transactions.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="min-h-full py-1">
        <div className="space-y-6">
          <div className="rounded-[26px] border border-[#E8EEF9] bg-white px-6 py-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h1 className="text-[34px] font-semibold tracking-[-0.03em] text-[#1A5EF9]">
                  Banking Management
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-[#7D8998]">
                  Track branch-level banking activity with a clean daybook.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleExport}
                  disabled={transactions.length === 0}
                  className="h-11 rounded-[12px] border-[#E8EEF9] px-6 text-[#1A5EF9] hover:bg-[#EEF4FF] hover:text-[#0E4FDE]"
                >
                  Export to CSV
                </Button>
                <Button
                  type="button"
                  onClick={() => setIsDialogOpen(true)}
                  className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
                  style={{
                    display:
                      (localStorage.getItem("role") || "").toLowerCase() ===
                      "admin"
                        ? "none"
                        : undefined,
                  }}
                >
                  Add Transaction
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="grid w-full flex-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Select
                  value={selectedBranch || "__all__"}
                  onValueChange={(value) => {
                    const nextBranch = value === "__all__" ? "" : value;
                    setSelectedBranch(nextBranch);
                    setSelectedCounter("");
                  }}
                >
                  <SelectTrigger className="h-12 rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]">
                    <SelectValue placeholder="Branch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All branches</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem
                        key={branch.branchId}
                        value={String(branch.branchId)}
                      >
                        {branch.branchName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedCounter || "__all__"}
                  onValueChange={(value) =>
                    setSelectedCounter(value === "__all__" ? "" : value)
                  }
                  disabled={!selectedBranch}
                >
                  <SelectTrigger className="h-12 rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF] disabled:opacity-60">
                    <SelectValue
                      placeholder={
                        selectedBranch ? "Counter" : "Select branch first"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All counters</SelectItem>
                    {counters.map((counter) => (
                      <SelectItem key={counter.id} value={String(counter.id)}>
                        {counter.counterName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedStaff || "__all__"}
                  onValueChange={(value) =>
                    setSelectedStaff(value === "__all__" ? "" : value)
                  }
                >
                  <SelectTrigger className="h-12 rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]">
                    <SelectValue placeholder="Staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All staff</SelectItem>
                    {staffs.map((staff) => (
                      <SelectItem key={staff.id} value={String(staff.id)}>
                        {staff.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="relative">
                  <CalendarDays className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#91A0B8]" />
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(event) => setSelectedDate(event.target.value)}
                    className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pr-11 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 lg:ml-4">
                {(selectedBranch || selectedCounter || selectedStaff || selectedDate) && (
                  <button
                    onClick={handleResetFilters}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    Reset
                  </button>
                )}
                <div className="rounded-[14px] bg-[#F5F8FD] px-4 py-3 text-sm text-[#7D8998] whitespace-nowrap">
                  <span className="font-semibold text-[#253650]">
                    {totalElements}
                  </span>{" "}
                  transactions found
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
            <div className="flex flex-col gap-4 border-b border-[#E8EEF9] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#21314A]">
                  Daily Banking Register
                </h2>
                <p className="mt-1 text-sm text-[#7D8998]">
                  Review deposits, withdrawals, and transfers across branches.
                </p>
              </div>
              <PaginationComponent
                currentPage={
                  bankingTransactionsData?.transactions?.pageNumber ??
                  currentPage
                }
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>

            <BankingTransactionsTable
              transactions={transactions}
              isLoading={isBankingTransactionsLoading}
              totals={totals}
              totalElements={totalElements}
              totalPages={totalPages}
              currentPage={currentPage}
              pageSize={pageSize}
              serverPageNumber={
                bankingTransactionsData?.transactions?.pageNumber
              }
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              formatTableDate={formatTableDate}
              formatCurrency={formatCurrency}
            />
          </div>
        </div>
      </div>

      <AddBankingTransactionDialog
        open={isDialogOpen}
        formState={formState}
        isSubmitting={isPostingTransaction}
        transactionTypes={
          TRANSACTION_TYPES_BY_MODE[formState.mode as TransactionMode] ?? []
        }
        modeOptions={MODE_OPTIONS}
        categoryOptions={CATEGORY_OPTIONS}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            resetForm();
          }
        }}
        onFieldChange={updateForm}
        onSubmit={handleCreateTransaction}
      />
    </>
  );
}

export default Banking;
